import { useCallback, useState } from 'react';
import { ApiError } from '../api/client';
import { orgsApi } from '../api/orgs';
import { discardOrgScopedCacheAndNavigate } from '../lib/orgCache';

export interface UseOrgSwitchOptions {
  /**
   * Called when a switch fails — e.g. to `refresh()` the org list after a
   * 403 `NOT_A_MEMBER` (membership revoked between listing and the click).
   */
  onError?: (message: string, code?: string) => void;
  /** Injectable for tests — forwarded to `discardOrgScopedCacheAndNavigate`. */
  navigate?: (path: string) => void;
}

export interface UseOrgSwitchResult {
  /** Calls `POST /api/session/switch-org`, then discards cached state and navigates. */
  switchTo: (orgId: string) => Promise<void>;
  isSwitching: boolean;
  /** Set on failure (e.g. `NOT_A_MEMBER`); never set on success (the app navigates away). */
  error: string | null;
}

/**
 * Drives the org-switch flow shared by the nav `OrgSwitcher` and the
 * post-login `OrgSelectionScreen` picker (ADR-0010 Phase 2):
 *
 * 1. `POST /api/session/switch-org` — the server verifies active membership
 *    and re-mints the session cookie; a `403 NOT_A_MEMBER` means the caller's
 *    membership in the target org was revoked between listing and the click.
 * 2. On success, `discardOrgScopedCacheAndNavigate` runs BEFORE anything else
 *    — this is the cross-org UI-leak-prevention step (the #1 risk of this
 *    feature) — and hard-navigates to `/dashboard`.
 * 3. On failure, `error` is set for the caller to render, and `onError` fires
 *    so the caller can refresh its org list.
 */
export function useOrgSwitch(options: UseOrgSwitchOptions = {}): UseOrgSwitchResult {
  const { onError, navigate } = options;
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchTo = useCallback(
    async (orgId: string) => {
      setIsSwitching(true);
      setError(null);
      try {
        await orgsApi.switchOrg(orgId);

        // MUST run before any navigation/UI update — discards every cached
        // org-scoped artifact so org A's data can never flash under org B.
        discardOrgScopedCacheAndNavigate({ navigate });
      } catch (err) {
        const code = err instanceof ApiError ? err.code : undefined;
        const message =
          code === 'NOT_A_MEMBER'
            ? 'You are no longer a member of that organization.'
            : err instanceof Error
              ? err.message
              : 'Could not switch organization. Try again.';
        setError(message);
        setIsSwitching(false);
        onError?.(message, code);
      }
    },
    [navigate, onError],
  );

  return { switchTo, isSwitching, error };
}
